import { UniqueIdentifier } from "@dnd-kit/core";
import { ImsPermissions } from "@redotech/account-common/permissions";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { ShipmentsSystemView } from "@redotech/redo-fulfillment-common/views/default-shipment-views";
import { useTranslation } from "@redotech/redo-i18n";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import TruckIconFilled from "@redotech/redo-icons/truck-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ShipmentViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { CREATE_VIEW_NAME } from "@redotech/redo-views-common/views";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";
import { useDefaultViewName } from "./get-default-view-name";

export const ShipmentsItem = memo(function ShipmentsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { views, folders, openEditFolderModal, updateOrdering } =
    useRequiredContext(ShipmentViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/shipments`;

  const hasPermissionToViewItem = useHasPermission(
    ImsPermissions.ACCESS_SHIPMENTS,
  );

  const defaultViewName = useDefaultViewName(views, ShipmentsSystemView.ALL);

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"shipments">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"shipments">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${encodeURIComponent(defaultViewName)}`,
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={TruckIconFilled}
      friendlyName={t("Shipments", {
        context: "inventory and purchasing navigation label",
      })}
      id="shipments"
      UnfilledIcon={TruckIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <ShipmentsAddViewItem href={`${urlPrefix}/${CREATE_VIEW_NAME}`} />
      <ShipmentsCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const ShipmentsAddViewItem = memo(function ShipmentsAddViewItem({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={t("+ Add new view", {
        context: "inventory and purchasing navigation label",
      })}
      id="shipments-create-view"
    />
  );
});

const ShipmentsCreateFolderItem = memo(function ShipmentsCreateFolderItem() {
  const { t } = useTranslation();
  const { openCreateFolderModal } = useRequiredContext(ShipmentViewsContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          openCreateFolderModal();
        },
      }}
      friendlyName={t("+ Add new folder", {
        context: "inventory and purchasing navigation label",
      })}
      id="shipments-create-folder"
    />
  );
});
